import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'rv-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  /** Persisted user preference */
  readonly mode = signal<ThemeMode>(this.getSavedMode());

  /** Resolved effective theme (always 'light' or 'dark') */
  readonly resolvedTheme = signal<'light' | 'dark'>(this.resolve(this.getSavedMode()));

  private mediaQuery: MediaQueryList | null = null;
  private mediaListener = () => this.apply(this.mode());

  constructor() {
    if (this.isBrowser) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }

    // React to mode changes
    effect(() => {
       this.onModeChanged(this.mode());
    });
  }

  private onModeChanged(mode: ThemeMode): void {
    if (this.isBrowser) {
      localStorage.setItem(STORAGE_KEY, mode);
    }
    this.apply(mode);
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
  }

  private apply(mode: ThemeMode): void {
    if (!this.isBrowser) return;

    const isDark =
      mode === 'dark' ||
      (mode === 'system' && this.mediaQuery?.matches === true);

    document.documentElement.classList.toggle('dark', isDark);
    this.resolvedTheme.set(isDark ? 'dark' : 'light');
  }

  private resolve(mode: ThemeMode): 'light' | 'dark' {
    if (!this.isBrowser) return 'light';
    if (mode === 'dark') return 'dark';
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }

  private getSavedMode(): ThemeMode {
    if (!isPlatformBrowser(this.platformId)) return 'system';
    const saved = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) as ThemeMode | null;
    return saved ?? 'system';
  }
}
